import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useApiErrorHandler from "../useApiErrorHandler";
import useLoading from "../useLoading";
import useAlerts from "../useAlerts";
import TestProgressAdminService from "../../services/dte/testProgressAdminService";

import {
  setTestProgressList,
  updateProgressInList,
  setFormFromDetail,
  updateFormField,
  setFormErrors,
  clearFormErrors,
  resetForm,
  setSubmit,
  openEditModal,
  closeEditModal,
} from "../../store/slices/dte/testProgressAdminSlice";

export const useTestProgressAdmin = () => {
  const dispatch: AppDispatch = useDispatch();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();

  // ==============================
  // STATE
  // ==============================
  const {
    list,
    form,
    formErrors,
    isSubmit,
    isEditModalOpen,
  } = useSelector((state: RootState) => state.testProgressAdmin);

  // ==============================
  // LIST
  // ==============================
  const fetchList = async () => {
    try {
      showLoadingIndicator();
      const response = await TestProgressAdminService.getAll();
      const { statusCode, data } = response;
      if (statusCode === 200) dispatch(setTestProgressList(data));
    } catch (err) {
      handle(err);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ==============================
  // LOAD DETAIL FOR EDIT
  // ==============================
  const loadForEdit = async (id: number) => {
    try {
      showLoadingIndicator();
      dispatch(clearFormErrors());
      const response = await TestProgressAdminService.getById(id);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setFormFromDetail(data));
        dispatch(openEditModal());
      }
    } catch (err) {
      handle(err);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ==============================
  // VALIDATION
  // ==============================
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (form.completedCount === null || form.completedCount < 0) {
      errors.completedCount = "Debe ingresar una cantidad válida.";
    }

    dispatch(setFormErrors(errors));
    return Object.keys(errors).length === 0;
  };

  // ==============================
  // UPDATE
  // ==============================
  const updateProgress = async () => {
    try {
      dispatch(clearFormErrors());
      if (!validate()) return;

      dispatch(setSubmit(true));

      const payload = {
        completedCount: form.completedCount,
        completed: form.completed,
      };

      const response = await TestProgressAdminService.update(form.id!, payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(updateProgressInList(data));
        dispatch(closeEditModal());
        dispatch(resetForm());
        showAlert(type, title, message);
      }
    } catch (err) {
      handle(err);
    } finally {
      dispatch(setSubmit(false));
    }
  };

  // ==============================
  // MODAL CONTROL
  // ==============================
  const openModalEdit = () => {
    dispatch(openEditModal());
  };

  const closeModalEdit = () => {
    dispatch(closeEditModal());
    dispatch(resetForm());
    dispatch(clearFormErrors());
  };

  // ==============================
  // FORM UPDATE
  // ==============================
  const updateField = (field: string, value: any) =>
    dispatch(updateFormField({ field: field as any, value }));

  const cleanFormErrors = () => dispatch(clearFormErrors());

  return {
    // state
    list,
    form,
    formErrors,
    isSubmit,
    isEditModalOpen,

    // CRUD
    fetchList,
    loadForEdit,
    updateProgress,

    // form
    updateField,
    cleanFormErrors,

    // modal
    openModalEdit,
    closeModalEdit,
  };
};
